import { computed, nextTick, ref, type Ref, type Reactive } from "vue";
import type { FormContext } from "vee-validate";
import { ShopListInput } from "@/generated/graphql";
import { localStorageGetChainCode, localStorageGetCustomerCode } from "@/common/localStorageUtil";
import { getChainList } from "@/common/graphql/chain";
import { getShopList } from "@/common/graphql/shop";
import { getWordList } from "@/common/graphql/expression";
import searchConditionStore from "@/store/searchConditionStore";
import { OPERATOR_LIST_FILE_NAME } from "../constants";

/**
 * 画面ロジックを扱う composable。
 */
export const useOperatorListInit = (
  overlay: Ref<boolean>,
  errdialog: Ref<boolean>,
  errMessage: Ref<string>,
  listLoading: Ref<boolean>,
  chainViewFlag: boolean,
  chainListItems: Ref<Array<{ chain_code: string; name: string }>>,
  chainSelectValues: Ref<{ chain_code: string; name: string } | null>,
  shopListItems: Ref<Array<{ shop_code: string; name: string }>>,
  shopSelectValues: Ref<{ shop_code: string; name: string } | null>,
  shopListInput: Reactive<ShopListInput>,
  groupRadioItems: Ref<Array<{ index: string; item: string }>>,
  groupSelectValues: Ref<{ index: string; item: string } | null>,
  authtypeRadioItems: Ref<Array<{ index: string; item: string }>>,
  authtypeSelectValues: Ref<{ index: string; item: string } | null>,
  searchFormRef: Ref<FormContext | null>,
  isOperatorGroupShopFlag: boolean,
  isOperatorGroupAdminFlag: boolean,
  isOperatorGroupOtherFlag: boolean,
  groupRadioItemsShop: Array<{ index: string; item: string }>,
  groupRadioItemsAdmin: Array<{ index: string; item: string }>,
  groupRadioItemsAll: Array<{ index: string; item: string }>,
  searchConditionId: Ref<string>,
  searchConditionName: Ref<string>,
  onClickGroup: () => void,
  onClickSearch: () => Promise<void>,
  isUpdateModeBack: () => boolean,
  listOptions: { page: number; itemsPerPage: number; sortBy: Array<{ key: string; order: "asc" | "desc" }> },
) => {
  //////////////////////////////////////////////////////////////////////////////
  // 初期画面
  //////////////////////////////////////////////////////////////////////////////
  const wordList = ref<Record<string, string>>({});

  // Fieldのname属性で使用される必要なラベルキー
  const requiredFieldLabelKeys = [
    'lbl_chain_name',
    'lbl_shop_name',
    'lbl_operator_group',
    'lbl_auth_type',
    'lbl_operator_id',
    'lbl_operator_regist_name',
  ] as const;

  // wordListが準備できているかをチェック（必要なラベルキーが全て存在するか）
  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return requiredFieldLabelKeys.every((key) => key in wordList.value);
  });

  async function runInitialLoad(): Promise<void> {
    try {
      overlay.value = true;
      Object.assign(wordList.value, await getWordList(localStorageGetChainCode(), OPERATOR_LIST_FILE_NAME, localStorageGetCustomerCode()));

      if (chainViewFlag) {
        chainListItems.value = await getChainList(localStorageGetCustomerCode());
        chainSelectValues.value!.chain_code = "";
        shopListInput.chain_code = "";
      } else {
        chainSelectValues.value!.chain_code = localStorageGetChainCode();
        shopListInput.chain_code = localStorageGetChainCode();
        shopListItems.value = await getShopList(shopListInput);
      }

      if (isOperatorGroupShopFlag) {
        groupRadioItems.value = groupRadioItemsShop;
      } else if (isOperatorGroupAdminFlag) {
        groupRadioItems.value = groupRadioItemsAdmin;
      } else if (isOperatorGroupOtherFlag) {
        groupRadioItems.value = groupRadioItemsAll;
      }
      groupSelectValues.value = groupRadioItems.value[0];

      onClickGroup();

      // Form が v-if="isWordListReady" で条件付きレンダリングされているため、
      // nextTick で DOM 更新を待ってから vee-validate の Field に値を同期する
      await nextTick();
      searchFormRef.value?.setFieldValue("operatorGroup", groupSelectValues.value);
      searchFormRef.value?.validateField("operatorGroup");
      searchFormRef.value?.setFieldValue("authType", authtypeSelectValues.value);
      searchFormRef.value?.validateField("authType");

      if (isUpdateModeBack()) {
        const searchCondition = (await searchConditionStore.getCondition(OPERATOR_LIST_FILE_NAME)) as Map<string, string> | null;
        await searchConditionStore.clearCondition(OPERATOR_LIST_FILE_NAME);
        if (searchCondition) {
          if (chainViewFlag) {
            chainSelectValues.value = chainListItems.value.find(element => element?.chain_code === searchCondition.get("chain_code")) || chainSelectValues.value;
            // vee-validate の Field に復元値を同期
            searchFormRef.value?.setFieldValue("chainName", chainSelectValues.value);
            if (chainSelectValues.value) {
              shopListInput.chain_code = chainSelectValues.value.chain_code || "";
              shopListItems.value = await getShopList(shopListInput);
            }
          }
          if (searchCondition.get("shop_code")) {
            shopSelectValues.value = shopListItems.value.find(element => element?.shop_code === searchCondition.get("shop_code")) || shopSelectValues.value;
          }
          // vee-validate の Field に復元値を同期
          searchFormRef.value?.setFieldValue("shopName", shopSelectValues.value);
          searchConditionId.value = searchCondition.get("operator_id") || "";
          searchConditionName.value = searchCondition.get("operator_name") || "";
          groupSelectValues.value = groupRadioItems.value.find(element => element.index === searchCondition.get("group")) || groupSelectValues.value;
          onClickGroup();
          // Form マウント後に vee-validate の Field に復元値を同期
          await nextTick();
          if (groupSelectValues.value) {
            searchFormRef.value?.setFieldValue("operatorGroup", groupSelectValues.value);
            searchFormRef.value?.validateField("operatorGroup");
          }
          authtypeSelectValues.value = authtypeRadioItems.value.find(element => element.index === searchCondition.get("auth_type")) || authtypeSelectValues.value;
          if (authtypeSelectValues.value) {
            searchFormRef.value?.setFieldValue("authType", authtypeSelectValues.value);
            searchFormRef.value?.validateField("authType");
          }
          // ページネーションを復元する
          // onClickSearch() はページを1にリセットするため、
          // 保存ページが2以上の場合は listOptions.page を直接設定し watch 経由で検索する
          const savedPage = searchCondition.get("page");
          if (savedPage && Number(savedPage) > 1) {
            listOptions.page = Number(savedPage);
          } else {
            onClickSearch();
          }
        }
      }
    } catch (err) {
      listLoading.value = false;
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
        console.error("未知のエラー:", err);
      }
      return;
    } finally {
      overlay.value = false;
    }
  }

  return {
    wordList,
    isWordListReady,
    runInitialLoad,
  };
};
